import {
  action,
  computed,
  makeAutoObservable,
  observable,
  runInAction,
} from "mobx";
import type { UserProfile} from "@common/types/user";
import { UserWithExperience } from "@common/types/user";
import type { UserService } from "@common/services/user-service";
import type { PurchasesApi } from "@web/src/app/services/purchases-api";
import HandledErrors, { HandledError } from "@common/consts/errors";
import type { CommonEvents } from "@common/services/common-events";
import type { UserReferralInfo } from "@common/types/referral";

interface UserStoreDependencies {
  localStorage: {
    setItem: (key: string, value: string) => Promise<void>;
    getItem: (key: string) => Promise<string | null>;
    removeItem: (key: string) => Promise<void>;
  };
  timezone: {
    getTimezone: () => string;
  };
  commonEvents: typeof CommonEvents;
  userService: UserService;
  purchasesApi: PurchasesApi;
  getAccessToken: () => string | null;
  onAccessTokenChange: (token: string | null) => void;
}

export default class UserStore {
  deps: UserStoreDependencies;

  hasCheckedLogin = false;
  subscriptionDetails: { managementUrl: string | null } | null | undefined =
    undefined; // null means no subscription active

  @observable
  userProfile: UserProfile | null = null;
  timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  @observable
  referralInfo: UserReferralInfo | null = null;

  @observable
  isLoadingReferralInfo = false;

  @computed
  get userId() {
    if (!this.userProfile) {
      // TODO: enforce log in
      return "anonymous";
    }
    return this.userProfile.id;
  }

  @computed
  get isLoggedIn() {
    return Boolean(this.userProfile && this.deps.getAccessToken());
  }

  @computed
  get isPremium() {
    return !!this.userProfile?.isPremium;
  }

  checkLoginPromise: Promise<void>;

  constructor(dependencies: UserStoreDependencies) {
    makeAutoObservable(this);

    this.deps = dependencies;

    const loadToken = async () => {
      try {
        const token = await this.deps.localStorage.getItem("accessToken");
        const profileStr = await this.deps.localStorage.getItem("userProfile");
        if (token && profileStr) {
          this.deps.onAccessTokenChange(token);
          this.userProfile = profileStr ? JSON.parse(profileStr) : null;
        }
      } catch (error) {
        console.error(error);
      } finally {
        runInAction(() => {
          this.hasCheckedLogin = true;
        });
      }
    };

    this.checkLoginPromise = loadToken();
  }

  async login(token: string, userProfile: UserProfile) {
    this.deps.onAccessTokenChange(token);
    await this.deps.localStorage.setItem("accessToken", token);
    this.setUserProfile(userProfile);
  }

  /**
   * @returns did login succeed?
   */
  async loginWithGoogle(
    idToken: string,
    pictureUrl: string | null,
    referralCode: string | undefined,
    platform: "iOS" | "android" | "web"
  ) {
    try {
      const verifyResponse = await this.deps.userService.verifyGoogleToken(
        idToken,
        referralCode,
        platform
      );
      if (verifyResponse?.success) {
        let userProfile = {
          ...verifyResponse.user,
          picture: pictureUrl ?? "",
        };
        await this.login(verifyResponse.token, userProfile);
        return true; // success
      }

      throw new Error("Google token could not be verified");
    } catch (error) {
      if (!(error instanceof HandledError)) throw error;
      if (error.isError(HandledErrors.EMAIL_TAKEN)) {
        this.deps.commonEvents.emit("PUBLISH_ALERT", {
          title: "Email Taken",
          content:
            "The associated email is already in use. Please sign in with a different account.",
        });
        return;
      }

      // Unhandled here
      throw error;
    }
  }

  /**
   * @returns did login succeed?
   */
  async loginWithGoogleAuthCode(
    authCode: string,
    referralCode: string | undefined,
    platform: "web" // "iOS" | "android" |
  ) {
    try {
      const verifyResponse = await this.deps.userService.verifyGoogleAuthCode(
        authCode,
        referralCode,
        platform
      );
      if (verifyResponse?.success) {
        let userProfile = {
          ...verifyResponse.user,
          picture: verifyResponse.picture || "", // Use picture from the response
        };
        await this.login(verifyResponse.token, userProfile);
        return true; // success
      }

      throw new Error("Google authorization code could not be verified");
    } catch (error) {
      if (!(error instanceof HandledError)) throw error;
      if (error.isError(HandledErrors.EMAIL_TAKEN)) {
        this.deps.commonEvents.emit("PUBLISH_ALERT", {
          title: "Email Taken",
          content:
            "The associated email is already in use. Please sign in with a different account.",
        });
        return;
      }

      // Unhandled here
      throw error;
    }
  }

  /**
   * @returns did login succeed?
   */
  async loginWithApple(
    authorizationCode: string,
    fullName: string | undefined,
    referralCode: string | undefined,
    platform: "iOS" | "android" | "web"
  ) {
    try {
      const verifyResponse = await this.deps.userService.verifyAppleToken(
        authorizationCode,
        fullName,
        referralCode,
        platform
      );
      if (verifyResponse?.success) {
        let userProfile = {
          ...verifyResponse.user,
          picture: "", // TODO: no picture
        };
        await this.login(verifyResponse.token, userProfile);
        return true; // success
      }

      throw new Error("Apple token could not be verified");
    } catch (error) {
      if (!(error instanceof HandledError)) throw error;
      if (error.isError(HandledErrors.APPLE_SIGN_IN_RESET_REQUIRED)) {
        this.deps.commonEvents.emit("PUBLISH_ALERT", {
          title: "Apple Sign In Error",
          content:
            "Your Apple Sign In can no longer be used to create a new account. Please use other sign-in methods, or contact support for assistance.",
        });
        return;
      }

      if (error.isError(HandledErrors.EMAIL_TAKEN)) {
        this.deps.commonEvents.emit("PUBLISH_ALERT", {
          title: "Email Taken",
          content:
            "The associated email is already in use. Please sign in with a different account.",
        });
        return;
      }

      // Unhandled here
      throw error;
    }
  }

  async logout() {
    this.setUserProfile(null);
    this.deps.onAccessTokenChange(null);
    await this.deps.localStorage.removeItem("accessToken");
  }

  async setUserProfile(userProfile: UserProfile | null) {
    this.userProfile = userProfile;
    if (userProfile) {
      await this.deps.localStorage.setItem(
        "userProfile",
        JSON.stringify(userProfile)
      );
    } else {
      await this.deps.localStorage.removeItem("userProfile");
    }
  }

  setTimezone(timezone: string) {
    this.timezone = timezone;
  }

  loadSettings() {
    this.timezone = this.deps.timezone.getTimezone();
  }

  async setWaitlist() {
    await this.deps.userService.setWaitlist(this.userId);
    await this.refreshUser();
  }

  async setNotBrandNew() {
    if (!this.userProfile) return;
    this.userProfile.seenOnboarding = "1.0.0"; // immediate so that onboarding doesn't restart
    await this.deps.userService.setOnboardingSeen(this.userProfile.id);
    await this.refreshUser();
  }

  async refreshUser() {
    return new Promise<void>(async (resolve) => {
      if (!this.userProfile) return;
      const updatedUser = await this.deps.userService.getUser(
        this.userProfile.id
      );
      runInAction(() => {
        this.setUserProfile({ ...this.userProfile!, ...updatedUser });
        resolve();
      });
    });
  }

  async refreshUserIsPremium() {
    return new Promise<void>(async (resolve) => {
      if (!this.userProfile) return;
      const isPremium = await this.deps.purchasesApi.getUserIsPremium();
      runInAction(() => {
        this.setUserProfile({ ...this.userProfile!, isPremium });
        resolve();
      });
    });
  }

  async refreshUserSubscriptionDetails() {
    const details = await this.deps.purchasesApi.getUserSubscriptionDetails();
    runInAction(() => {
      this.subscriptionDetails = details;
    });
  }

  @action
  async fetchReferralInfo() {
    if (!this.isLoggedIn) return null;

    try {
      this.isLoadingReferralInfo = true;
      const referralInfo = await this.deps.userService.getUserReferralInfo();

      runInAction(() => {
        this.referralInfo = referralInfo;
      });

      return referralInfo;
    } catch (error) {
      console.error("Failed to fetch referral info:", error);
      return null;
    } finally {
      runInAction(() => {
        this.isLoadingReferralInfo = false;
      });
    }
  }

  async deleteUser(confirmationEmail: string) {
    const success = await this.deps.userService.deleteUser(confirmationEmail);
    success && (await this.logout());
    return success;
  }
}
